import {
  BankOutlined,
  CheckOutlined,
  DownOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { history } from '@umijs/max';
import { App, Dropdown, Input, Spin, theme } from 'antd';
import React from 'react';
import { ROUTES } from '@/constants/routes';
import type { Park } from '@/services/bff/types/park';
import {
  ParkBootstrapStatus,
  selectOperatorPark,
  useParkBootstrap,
} from '@/services/parkBootstrap';
import styles from './index.module.less';

const PARK_SEARCH_THRESHOLD = 6;

const matchesPark = (park: Park, keyword: string) => {
  if (!keyword) return true;
  const haystack = `${park.name} ${park.code || ''}`.toLowerCase();
  return haystack.includes(keyword);
};

const ParkSwitcherItem: React.FC<{
  park: Park;
  current?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ park, current, disabled, onClick }) => {
  const { token } = theme.useToken();
  return (
    <button
      type="button"
      className={`${styles.item} ${current ? styles.itemCurrent : ''}`}
      disabled={disabled}
      aria-current={current ? 'true' : undefined}
      onClick={onClick}
    >
      <BankOutlined
        className={styles.icon}
        style={{ color: token.colorTextSecondary, fontSize: 14 }}
      />
      <span className={styles.itemName} title={park.name}>
        {park.name}
      </span>
      {current ? (
        <CheckOutlined
          className={styles.check}
          style={{ color: token.colorPrimary, fontSize: 14 }}
        />
      ) : null}
    </button>
  );
};

export const ParkSwitcher: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { activeParks, status, tenantUuid, currentParkUuid } =
    useParkBootstrap();
  const [open, setOpen] = React.useState(false);
  const [keyword, setKeyword] = React.useState('');
  const [switching, setSwitching] = React.useState(false);
  const switchingRef = React.useRef(false);

  const currentPark =
    activeParks.find((park) => park.uuid === currentParkUuid) || activeParks[0];
  const showSearch = activeParks.length > PARK_SEARCH_THRESHOLD;
  const normalizedKeyword = keyword.trim().toLowerCase();
  const listedParks = activeParks.filter((park) =>
    matchesPark(park, normalizedKeyword),
  );

  const tokenVars = {
    '--park-switcher-hover': token.colorFillTertiary,
    '--park-switcher-current-bg': token.colorPrimaryBg,
    '--park-switcher-focus': token.colorPrimaryBorder,
    '--park-switcher-split': token.colorSplit,
  } as React.CSSProperties;

  const closePanel = () => {
    setOpen(false);
    setKeyword('');
  };

  const switchPark = (parkUuid: string) => {
    if (switchingRef.current) return;
    if (parkUuid === currentPark?.uuid) {
      closePanel();
      return;
    }

    switchingRef.current = true;
    setSwitching(true);
    try {
      if (!tenantUuid || !selectOperatorPark(tenantUuid, parkUuid)) {
        message.error('园区切换失败，请稍后重试');
        return;
      }
      closePanel();
    } catch {
      message.error('园区切换失败，请稍后重试');
    } finally {
      switchingRef.current = false;
      setSwitching(false);
    }
  };

  const openCreatePark = () => {
    closePanel();
    history.push(ROUTES.park.create);
  };

  const openManagePark = () => {
    closePanel();
    history.push(ROUTES.park.profile);
  };

  if (status !== ParkBootstrapStatus.READY || !currentPark) {
    return null;
  }

  return (
    <div className={styles.root} style={tokenVars}>
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        open={open}
        disabled={switching}
        destroyOnHidden
        getPopupContainer={() => document.body}
        styles={{ root: { padding: 0 } }}
        onOpenChange={(next) => {
          if (switching) return;
          setOpen(next);
          if (!next) setKeyword('');
        }}
        popupRender={() => (
          <div
            className={styles.panel}
            style={{
              background: token.colorBgElevated,
              boxShadow: token.boxShadowSecondary,
              border: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            {showSearch ? (
              <div className={styles.search}>
                <Input
                  allowClear
                  size="small"
                  value={keyword}
                  placeholder="搜索园区名称"
                  prefix={
                    <SearchOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  onChange={(event) => setKeyword(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                />
              </div>
            ) : null}
            <div
              className={styles.sectionLabel}
              style={{ color: token.colorTextSecondary }}
            >
              切换园区
            </div>
            {listedParks.length > 0 ? (
              <div className={styles.list}>
                {listedParks.map((park) => (
                  <ParkSwitcherItem
                    key={park.uuid}
                    park={park}
                    current={park.uuid === currentPark.uuid}
                    disabled={switching}
                    onClick={() => switchPark(park.uuid)}
                  />
                ))}
              </div>
            ) : (
              <div
                className={styles.empty}
                style={{ color: token.colorTextSecondary }}
              >
                未找到匹配园区
              </div>
            )}
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.manage}
                onClick={openCreatePark}
              >
                <PlusOutlined
                  style={{ color: token.colorTextSecondary, fontSize: 14 }}
                />
                创建园区
              </button>
              <button
                type="button"
                className={styles.manage}
                onClick={openManagePark}
              >
                <SettingOutlined
                  style={{ color: token.colorTextSecondary, fontSize: 14 }}
                />
                管理园区
              </button>
            </div>
          </div>
        )}
      >
        <button
          type="button"
          className={`${styles.trigger} ${styles.triggerInteractive}`}
          aria-label="当前园区"
          title={currentPark.name}
          disabled={switching}
        >
          <BankOutlined
            className={styles.icon}
            style={{ color: token.colorTextSecondary, fontSize: 14 }}
          />
          <span className={styles.name} title={currentPark.name}>
            {currentPark.name}
          </span>
          {switching ? (
            <Spin className={styles.spin} size="small" />
          ) : (
            <DownOutlined
              className={styles.caret}
              style={{ color: token.colorTextSecondary, fontSize: 10 }}
            />
          )}
        </button>
      </Dropdown>
    </div>
  );
};
